import { AxiosResponse } from 'axios';
import { DEVICE_INFO_API_URL } from '../../../config';
import deviceInfoAxios from '../../../utils/axios/deviceInfo.axios';

export type PeriodKey = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';

export type ReviewsSearchParams = {
  period: PeriodKey;
  minScore?: number | null;
  from?: string; // ISO (used when period='custom')
  to?: string;   // ISO (used when period='custom')
  sinceLastReport?: boolean; // only for report/xlsx (adds the 'new since' section)
  mark?: boolean;            // mark lastReportAt = now on report fetch
};

export type ReviewDoc = {
  score?: number | null;
  text: string;
  url: string;
  postedAt?: string | null;
  site?: string;
  author?: string | null;
  sentiment?: 'positive' | 'neutral' | 'negative' | null;
  topics?: string[];
};

export type ReportMetrics = {
  numberOfReviews: number;
  averageScoreInPeriod: number | null;
  averageScoreOverall: number | null;
  sentiments: { positive: number; neutral: number; negative: number };
  topTopics: { topic: string; count: number }[];
};

export type ReportResponse = {
  period: { start: string; end: string };
  lastReportAt?: string | null;
  newSinceLastReport?: { lastReportAt?: string | null; count: number | null; items: ReviewDoc[] };
  metrics: ReportMetrics;
  reviews: ReviewDoc[];
};

const buildQS = (params: Record<string, any>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qs.set(k, String(v));
  });
  return qs.toString();
};

const base = `${DEVICE_INFO_API_URL}/reviews`;

// ------- Report (JSON) -------
export const tryToGetReviewsReport = (args: ReviewsSearchParams): Promise<AxiosResponse<ReportResponse>> => {
  const { period, minScore, from, to, sinceLastReport, mark } = args;
  const qs = buildQS({
    period,
    minScore,
    from,
    to,
    sinceLastReport: sinceLastReport ? 1 : undefined,
    mark: mark ? 1 : undefined,
  });
  const url = `${base}/report?${qs}`;
  return deviceInfoAxios.get(url);
};

// ------- Download CSV -------
export const tryToDownloadReviewsCsv = (args: Omit<ReviewsSearchParams, 'sinceLastReport' | 'mark'>) => {
  const { period, minScore, from, to } = args;
  const qs = buildQS({ period, minScore, from, to });
  const url = `${base}/download?${qs}`;
  return deviceInfoAxios.get<Blob>(url, { responseType: 'blob' });
};

// ------- Download XLSX (wide columns + summary) -------
export const tryToDownloadReviewsXlsx = (args: ReviewsSearchParams) => {
  const { period, minScore, from, to, sinceLastReport } = args;
  const qs = buildQS({
    period,
    minScore,
    from,
    to,
    sinceLastReport: sinceLastReport ? 1 : undefined,
  });
  const url = `${base}/download.xlsx?${qs}`;
  return deviceInfoAxios.get<Blob>(url, { responseType: 'blob' });
};

// ------- Force harvest since date -------
export const tryToHarvestNow = (sinceISO: string) => {
  const url = `${base}/harvest/now?since=${encodeURIComponent(sinceISO)}`;
  return deviceInfoAxios.get(url);
};

// ------- Backfill months -------
export const tryToBackfillMonths = (months = 1) => {
  const url = `${base}/harvest/backfill?months=${months}`;
  return deviceInfoAxios.get(url);
};
